import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Account, AuthActions, AuthSelectors } from "@ha/feature/auth";
import { PushNotificationsService } from "@ha/feature/notification";
import { PwaService, PwaState } from "@ha/util/pwa";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap, tap, timer, withLatestFrom } from "rxjs";

import { HaModalService } from "@ha/ui/common";
import { PermitCreateSelectors } from "libs/feature/permit/src/lib/permit-create/states/permit-create.selectors";
import { environment } from "../environments/environment";
import { MustEnableMfaComponent } from "./auth/components/must-enable-mfa.component";
import { CurrentUserActions } from "./core/states/current-user.actions";
import { CurrentUserState } from "./core/states/current-user.state";
import { SettingsStateActions } from "./core/states/settings.actions";
import { PersonalNumberComponent } from "./personal-number/personal-number.component";
import { UserAcceptNewAgreementComponent } from "./user-accept-new-agreement/user-accept-new-agreement.component";
import { UserActivateState } from "./user-activate/states/user-activate.state";
import { UserStateActions } from "./user/state/user.actions";
import { UserState } from "./user/state/user.state";

@UntilDestroy()
@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
    public screenSize: string;
    public printMode = false;

    @Select(PwaState.isOffline)
    public isOffline$: Observable<boolean>;

    @Select(AuthSelectors.isAuthenticated)
    public isAuthenticated$: Observable<boolean>;

    @Select(CurrentUserState.noUserFound)
    public noUserFound$: Observable<boolean>;

    @Select(AuthSelectors.currentAccount)
    public currentAccount$: Observable<Account>;

    @Select(UserState.agreementLatestPublishedVersionNumber)
    public agreementLatestPublishedVersionNumber$: Observable<number>;

    public showApp$: Observable<boolean>;
    public startupRecoveryVisible$: Observable<boolean>;

    public showModalForPersonalNumber = true;

    constructor(
        private pwaService: PwaService,
        private activatedRoute: ActivatedRoute,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private store: Store,
        private haModalService: HaModalService,
        private pushNotificationsService: PushNotificationsService,
    ) {
        const isUserInitialized$ = this.actions$.pipe(
            ofActionSuccessful(CurrentUserActions.Get),
            map(() => true),
            startWith(false),
        );

        this.showApp$ = combineLatest([isUserInitialized$, this.isAuthenticated$]).pipe(
            map(([isAppInitialized, isAuthenticated]): boolean => isAppInitialized && isAuthenticated),
        );

        this.startupRecoveryVisible$ = combineLatest([
            this.showApp$,
            this.isAuthenticated$,
            this.noUserFound$,
            this.currentAccount$.pipe(startWith(undefined)),
        ]).pipe(
            switchMap(([showApp, isAuthenticated, noUserFound, currentAccount]) => {
                const shouldShowRecovery = isAuthenticated === true
                    && showApp === false
                    && noUserFound !== true
                    && currentAccount?.accessToApplications?.app !== false;

                return shouldShowRecovery ? timer(7000).pipe(map(() => true)) : of(false);
            }),
            startWith(false),
            distinctUntilChanged(),
        );

        if (!environment.production) {
            (window as any).ngStore = this.store;
            (window as any).PermitCreateSelectors = PermitCreateSelectors;
        }
    }

    public ngOnInit(): void {
        this.pwaService.init(true);
        this.setScreenSize();

        this.pushNotificationsService.initializeMessageListening();

        this.haModalService.onClose.subscribe(() => {
            this.currentAccount$.pipe(
                untilDestroyed(this),
                distinctUntilChanged((prev, curr) => prev.identityUserId === curr.identityUserId),
                filter((currentAccount) =>
                    !!currentAccount
                    && !!currentAccount.user
                    && currentAccount.hasBACertificate
                    && currentAccount.accessToApplications.app
                    && currentAccount.user.personalNumber == null
                    && currentAccount.user.hasForeignPersonalNumber == false
                ),
                tap(() => {
                    this.store.dispatch(new AuthActions.GetCurrentAccount());
                }),
            ).subscribe(() => {
                if (this.showModalForPersonalNumber) {
                    this.haModalService.open(PersonalNumberComponent, false);
                    this.showModalForPersonalNumber = false;
                }
            });
        });
        this.showModalIfNoPersonalNumber();
        this.showApproveModalIfNewAgreement();
        this.showForceLogoutIfMfaRequired();

        this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
            if (params.print) {
                this.printMode = true;
            }
        });

        // Transloco sucks workaround
        this.actions$.pipe(ofActionSuccessful(SettingsStateActions.ChangeLanguage), untilDestroyed(this)).subscribe(
            () => this.cdr.markForCheck()
        );
    }

    @HostListener("window:resize")
    public onResize(): void {
        this.setScreenSize();
    }

    public retryStartup(): void {
        this.store.dispatch([
            new AuthActions.GetCurrentAccount(),
            new CurrentUserActions.Get(),
        ]);
    }

    public async clearLocalBrowserDataAndReload(): Promise<void> {
        try {
            localStorage.clear();
            sessionStorage.clear();

            if ("caches" in window) {
                const cacheKeys = await caches.keys();
                await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
            }

            if ("serviceWorker" in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map((registration) => registration.unregister()));
            }
        } finally {
            window.location.assign(window.location.origin);
        }
    }

    private setScreenSize() {
        this.screenSize = `${window.innerWidth} ×  ${window.innerHeight}`;
    }

    private showModalIfNoPersonalNumber() {
        combineLatest([this.router.events, this.showApp$]).pipe(
            untilDestroyed(this),
            filter(([routerEvent, showApp]) => routerEvent instanceof NavigationEnd && showApp),
            tap(() => this.store.dispatch(new AuthActions.GetCurrentAccount())),
        ).subscribe();

        this.currentAccount$.pipe(
            untilDestroyed(this),
            withLatestFrom(this.agreementLatestPublishedVersionNumber$),
            filter(([currentAccount]) => !!currentAccount?.user),
            tap(([currentAccount, agreementLatestPublishedVersionNumber]) => {
                const agreementAcceptedVersionNumber = +currentAccount.user.agreementVersionId;
                const acceptedDuringImpersonation = this.store.selectSnapshot(
                    UserActivateState.newAgreementAcceptedDuringImpersonation,
                );

                if (
                    this.showModalForPersonalNumber == true
                    && currentAccount.hasBACertificate
                    && currentAccount.accessToApplications.app
                    && currentAccount.user.personalNumber === null
                    && !currentAccount.user.hasForeignPersonalNumber
                    && agreementLatestPublishedVersionNumber !== undefined
                    && !(agreementLatestPublishedVersionNumber > agreementAcceptedVersionNumber
                        && !acceptedDuringImpersonation)
                ) {
                    this.haModalService.open(PersonalNumberComponent, false);
                    this.showModalForPersonalNumber = false;
                }
            }),
        ).subscribe();
    }

    private showApproveModalIfNewAgreement() {
        let doCheckToShowModal = false;

        combineLatest([this.router.events, this.showApp$]).pipe(untilDestroyed(this)).subscribe(([event, showApp]) => {
            if (showApp && event instanceof NavigationEnd) {
                doCheckToShowModal = true;
                this.store.dispatch(new AuthActions.GetCurrentAccount());
                this.store.dispatch(new UserStateActions.GetAgreementLatestPublishedVersionNumber());
            }
        });

        combineLatest([this.currentAccount$, this.agreementLatestPublishedVersionNumber$]).pipe(untilDestroyed(this))
            .subscribe(([currentAccount, agreementLatestPublishedVersionNumber]) => {
                const agreementAcceptedVersionNumber = +currentAccount?.user?.agreementVersionId;

                if (doCheckToShowModal) {
                    const acceptedDuringImpersonation = this.store.selectSnapshot(
                        UserActivateState.newAgreementAcceptedDuringImpersonation,
                    );
                    if (
                        agreementLatestPublishedVersionNumber > agreementAcceptedVersionNumber
                        && !acceptedDuringImpersonation
                    ) {
                        this.haModalService.open(UserAcceptNewAgreementComponent, false);
                    }
                    doCheckToShowModal = false;
                }
            });
    }

    private showForceLogoutIfMfaRequired() {
        let doCheckToForceLogout = false;

        combineLatest([this.router.events, this.showApp$]).pipe(untilDestroyed(this)).subscribe(([event, showApp]) => {
            if (showApp && event instanceof NavigationEnd) {
                doCheckToForceLogout = true;
                this.store.dispatch(new AuthActions.GetCurrentAccount());
            }
        });

        this.currentAccount$.pipe(untilDestroyed(this)).subscribe((currentAccount) => {
            if (doCheckToForceLogout && currentAccount) {
                if (currentAccount.mustEnableTwoFactor === true && !currentAccount.twoFactorEnabled) {
                    this.haModalService.open(MustEnableMfaComponent, false);
                }
                doCheckToForceLogout = false;
            }
        });
    }
}
